import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { ulid } from 'ulid';
import { useRuntime } from './context';
import { useHasModule, useRemote } from './useRemote';
import type { Remote } from './useRemote';
import { ApiFailure } from '../api/errors';
import { formatClock } from '../mirror/time';
import type { DataRequest } from '../wire';
import { DataRequestList, DataRequestRun } from '../wire';
import type { T } from '../messages';
import type { UiLocale } from '../messages/locale';

/**
 * **"Your data", on the owner's screen** — spec §7.6, `docs/guest-site-plan.md`
 * GS5, migration 0061.
 *
 * A guest asked for a copy of her data, or asked to be forgotten. It arrives
 * here as a task beside the other kinds §8.3 puts on that screen — a slot the
 * concierge took, a parcel the maker refused, a room sold twice — because it
 * is the same kind of sentence: *somebody needs you to decide something, and
 * there is a clock on it*. The clock is the thirty days the privacy page
 * promises, and the task shows how many are left.
 *
 * ## The one button, and who may press it
 *
 * "Run it" is `POST /v1/data-requests/{id}/run`, and that route is
 * **owner-only** (`registry.ts`: `owner: true`). A front-desk seat sees the
 * task — it is useful to know a guest is waiting — and sees the button
 * disabled with the reason, rather than a `403` after the tap. The argument
 * for the gate is on the route; the argument for showing the task anyway is
 * that hiding an obligation from the person who answers the telephone is how
 * it gets to day thirty-one.
 *
 * ## What comes back
 *
 * For a deletion: counts, never values — "2 identities, 3 preferences". For an
 * export: the document itself, which this screen turns into a file to save
 * (`data:` URL, no server, no email), because handing it over is the owner's
 * act and the only channel today is the owner's own hands.
 */

export function useDataRequests(): Remote<DataRequestList> {
  const runtime = useRuntime();
  const entitled = useHasModule('clients');
  return useRemote(
    (signal) =>
      runtime.api.request({
        method: 'GET',
        path: '/data-requests',
        query: { status: 'open' },
        schema: DataRequestList,
        signal,
      }),
    [],
    {
      cue: (type) => type.startsWith('data_request.'),
      // Unhurried: an ask has thirty days on it and the stream carries the cue.
      pollMs: 120_000,
      enabled: entitled,
    },
  );
}

interface Refusal {
  requestId: string;
  message: string;
}

export interface DataRequestOutcome {
  requestId: string;
  /** The export document, as a `data:` URL the desk can save, when it was one. */
  fileHref: string | null;
  fileName: string;
  summary: string;
}

export function useRunDataRequest(
  onDone: () => void,
  t: T,
): {
  run: (request: DataRequest) => Promise<void>;
  busy: string | null;
  refusal: Refusal | null;
  outcome: DataRequestOutcome | null;
} {
  const runtime = useRuntime();
  const [busy, setBusy] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [outcome, setOutcome] = useState<DataRequestOutcome | null>(null);

  const run = useCallback(
    async (request: DataRequest): Promise<void> => {
      setBusy(request.data_request_id);
      setRefusal(null);
      try {
        const answer = await runtime.api.request({
          method: 'POST',
          path: `/data-requests/${request.data_request_id}/run`,
          body: {},
          idempotencyKey: ulid(),
          schema: DataRequestRun,
        });
        if (answer.export !== undefined) {
          const json = JSON.stringify(answer.export, null, 2);
          setOutcome({
            requestId: request.data_request_id,
            fileHref: `data:application/json;charset=utf-8,${encodeURIComponent(json)}`,
            fileName: `${request.guest_name.replace(/[^\w-]+/g, '_') || 'guest'}-data.json`,
            summary: t('tasks.data.exported', { name: request.guest_name }),
          });
        } else {
          const wiped = answer.anonymized ?? {};
          setOutcome({
            requestId: request.data_request_id,
            fileHref: null,
            fileName: '',
            summary: t('tasks.data.forgotten', {
              identities: wiped['identities'] ?? 0,
              preferences: wiped['preferences'] ?? 0,
            }),
          });
        }
      } catch (e) {
        // The server's sentence, verbatim — a `409` says the task was already
        // closed by somebody else, which is the one thing this screen does
        // not know.
        if (e instanceof ApiFailure || e instanceof Error) {
          setRefusal({ requestId: request.data_request_id, message: e.message });
        }
      } finally {
        setBusy(null);
        onDone();
      }
    },
    [runtime, onDone, t],
  );

  return { run, busy, refusal, outcome };
}

export function DataRequestAttention({
  requests,
  run,
  busy,
  refusal,
  outcome,
  isOwner,
  timezone,
  t,
  locale,
}: {
  requests: readonly DataRequest[];
  run: (request: DataRequest) => Promise<void>;
  busy: string | null;
  refusal: Refusal | null;
  outcome: DataRequestOutcome | null;
  isOwner: boolean;
  timezone: string;
  t: T;
  locale: UiLocale;
}): ReactNode {
  if (requests.length === 0 && outcome === null) return null;
  return (
    <ul className="task-list task-list--data">
      {outcome === null ? null : (
        <li className="task task--data task--done">
          <p className="task__message">{outcome.summary}</p>
          {outcome.fileHref === null ? null : (
            <div className="task__actions">
              <a
                className="button button--primary"
                href={outcome.fileHref}
                download={outcome.fileName}
              >
                {t('tasks.data.saveFile')}
              </a>
            </div>
          )}
        </li>
      )}
      {requests.map((request) => (
        <li key={request.data_request_id} className="task task--data">
          <h2>
            {request.kind === 'delete'
              ? t('tasks.data.deleteTitle', { name: request.guest_name })
              : t('tasks.data.exportTitle', { name: request.guest_name })}
          </h2>
          <p className="task__message">
            {t('tasks.data.asked', {
              clock: formatClock(request.created_at, timezone, locale),
              days: request.days_left ?? 0,
            })}
          </p>
          <p className="task__message muted">
            {request.kind === 'delete' ? t('tasks.data.deleteBody') : t('tasks.data.exportBody')}
          </p>
          {refusal !== null && refusal.requestId === request.data_request_id ? (
            <p className="error" role="alert">
              {refusal.message}
            </p>
          ) : null}
          <div className="task__actions">
            <button
              type="button"
              className="button button--primary"
              disabled={!isOwner || busy === request.data_request_id}
              onClick={() => void run(request)}
            >
              {request.kind === 'delete' ? t('tasks.data.runDelete') : t('tasks.data.runExport')}
            </button>
            {isOwner ? null : <span className="muted">{t('tasks.data.ownerOnly')}</span>}
          </div>
        </li>
      ))}
    </ul>
  );
}
